/**
 * Os SELOS do card de hotel — o eixo (recomendado / menor preço / mais perto),
 * os pessoais (favorito / você ficou aqui) e o destaque do agente. Extraído de
 * `hotel_planner_view.ts` (#3399 b) para o rótulo do eixo poder dizer o
 * recorte ("no cartão") sem estourar o teto de linhas da view.
 *
 * As palavras vêm do host (`HotelBadgesCopy`) e a distância também
 * (`distanceOf`): a régua de distância é a do wire, que o agente lê, e o kit
 * não a duplica.
 */
import type { HotelAxis, HotelBadgeSource, HotelBadgesCopy, HotelBadgeView } from '../models.js';

/**
 * Os três eixos da R-hotel-1, atribuídos de forma EXCLUSIVA — um hotel por
 * eixo, que é o "separando os 3" da R-hotel-3.
 *
 * A exclusividade não é enfeite. Com UM selo por card, um hotel que fosse ao
 * mesmo tempo o recomendado e o mais perto engoliria o terceiro eixo: "Mais
 * perto" sumiria da tela mesmo existindo um hotel mais perto que todos os
 * outros.
 *
 * Ensaio 14/09 (E2, selo 'Menor preço' errado): o eixo NUNCA é doado ao vice.
 * Um design anterior cedia "Mais perto"/"Menor preço" ao segundo colocado
 * quando o mínimo GLOBAL do eixo já carregava `recommended` — isso fazia um
 * card que NÃO é o mínimo entre as ofertas exibir "Menor preço" enquanto um
 * card mais barato ficava visível do lado sem selo nenhum, uma contradição na
 * tela. A regra agora é: o selo do eixo só existe quando o mínimo GLOBAL ainda
 * está livre.
 *
 * DUAS MECÂNICAS diferentes protegem essa regra, para os TRÊS selos que
 * suprimem o eixo (não confundir uma com a outra):
 * - `recommended` é PRÉ-SEMEADO neste Map antes de `claim` rodar (loop logo
 *   abaixo) — por isso o próprio `claim` VÊ o dono e nega o eixo a qualquer
 *   vice, aqui mesmo.
 * - `favorite`/`stayedBefore` NÃO entram neste Map — eles nem existem para
 *   `claim`, que pode legitimamente atribuir o eixo a uma oferta favoritada ou
 *   com estadia. Quem apaga essa atribuição da TELA é `offerBadges` (abaixo):
 *   `favorite`/`stayedBefore` retornam ANTES de olhar `axis`, então o selo do
 *   eixo nunca é desenhado NAQUELE card — e como o Map já decidiu o dono, o
 *   eixo não é reatribuído a mais ninguém. O efeito final é o mesmo (eixo sem
 *   selo visível), a origem é outra.
 *
 * A ordem é a de força: ① a curadoria (o `recommended` do fornecedor, que pode
 * marcar mais de um hotel), ② a menor diária, ③ o menor deslocamento — cada
 * eixo medido contra TODAS as ofertas, não só as que sobraram dos eixos mais
 * fortes. Empate fica com o primeiro da lista — a ordem que veio já é a
 * curadoria de quem montou, e desempatar por outro critério aqui inventaria
 * uma segunda regra invisível.
 *
 * Sem o dado do eixo não há selo: hotel de varredura sai mudo, como no
 * protótipo (`SeloDoEixo` devolve `null` sem `hotel.eixo`).
 */
export function hotelAxisByOffer<T extends HotelBadgeSource>(
  offers: readonly T[],
  /** A distância da oferta à âncora da busca — a régua canônica do host. */
  distanceOf: (offer: T) => number | undefined,
): ReadonlyMap<string, HotelAxis> {
  const axes = new Map<string, HotelAxis>();
  for (const offer of offers) {
    if (offer.recommended) axes.set(offer.offerId, 'recommended');
  }
  const claim = (axis: HotelAxis, valueOf: (offer: T) => number | undefined) => {
    let best: { id: string; value: number } | undefined;
    for (const offer of offers) {
      const value = valueOf(offer);
      if (value !== undefined && (best === undefined || value < best.value)) {
        best = { id: offer.offerId, value };
      }
    }
    // O mínimo GLOBAL já tem outro selo? O eixo fica sem dono — nunca doado
    // ao vice (ver a doc acima).
    if (best && !axes.has(best.id)) axes.set(best.id, axis);
  };
  claim('cheapest', (offer) => offer.price?.amount);
  claim('closest', distanceOf);
  return axes;
}

/** As chaves de `HotelBadgesCopy` que nomeiam um eixo. */
type AxisLabelKey = 'recommended' | 'cheapest' | 'closest' | 'cheapestOnCard' | 'closestOnCard';

const AXIS_LABEL: Readonly<Record<HotelAxis, AxisLabelKey>> = {
  recommended: 'recommended',
  cheapest: 'cheapest',
  closest: 'closest',
};

/** Os mesmos eixos quando o bloco é um RECORTE da busca (#3399 b): "Menor
 *  preço" medido sobre 6 de 101 é desmentido por quem abre o painel e vê um
 *  mais barato — o rótulo diz o recorte ("no cartão"). O recomendado não muda:
 *  é curadoria do fornecedor sobre a busca inteira, não um mínimo medido aqui. */
const AXIS_LABEL_ON_CARD: Readonly<Record<HotelAxis, AxisLabelKey>> = {
  recommended: 'recommended',
  cheapest: 'cheapestOnCard',
  closest: 'closestOnCard',
};

/**
 * O selo do EIXO, e é UM por card.
 *
 * No protótipo o eixo é campo singular (`hotel.eixo`, e o `SeloDoEixo` desenha
 * um só): o selo existe para fazer PARAR no card, não para descrever o que ele
 * é. Empilhados, três selos viram faixa na borda de um card de 21rem e param de
 * marcar qualquer coisa. Quem decide de quem é cada eixo é `hotelAxisByOffer`,
 * uma vez para a lista inteira; aqui só se pinta.
 *
 * Só o recomendado é SÓLIDO (`within`) — no protótipo é ele que carrega a marca
 * da VOLL. Os outros dois são `soft`.
 *
 * NENHUM chip de política aqui, e é regra do protótipo (`CardUnicoDoHotel`): a
 * política só aparece quando o hotel está FORA, como escudo sem palavra colado
 * no preço (`outOfPolicyLabel`). "Dentro da política" em chip gastaria a faixa
 * de selos com a informação que é a norma, não a exceção.
 *
 * O selo PESSOAL ("Você ficou aqui") entra ACIMA dos três eixos: a memória do
 * viajante vale mais que qualquer curadoria genérica, e continua sendo UM selo
 * por card — a oferta com `stayedBefore` pode ter GANHO um eixo em
 * `hotelAxisByOffer` (esse Map não pré-semeia `stayedBefore`, só
 * `recommended`), mas este `return` roda ANTES do `if (!axis)` abaixo e
 * desenha só o selo pessoal — o eixo fica sem representação na tela, sem ser
 * doado a mais ninguém (ver a doc de `hotelAxisByOffer`). Com `stayCount` ≥ 2
 * o selo carrega a contagem ("· N×"); 1 estadia fica sem número — "você ficou
 * aqui · 1×" leria como aviso, não como reconhecimento.
 *
 * O FAVORITO (item 1 F2) fica acima até do selo pessoal de estadia: estadia é
 * registro do que aconteceu, favorito é a escolha que o viajante declarou.
 * Colisão favorito+estadia = card único com o selo "Favorito" — o
 * `stayedBefore` continua no dado (e no ranking do servidor), só a pintura é
 * uma. Segue UM selo por card, sempre.
 *
 * O hotel com ACORDO (#6359) vem logo abaixo dos dois pessoais e acima do eixo.
 */
export function offerBadges(
  offer: HotelBadgeSource,
  axis: HotelAxis | undefined,
  copy: HotelBadgesCopy,
  axisOnCard = false,
): HotelBadgeView[] {
  if (offer.favorite) {
    return [
      {
        id: 'favorite',
        label: copy.favorite,
        // Sólido como os outros dois selos que fazem parar no card.
        tone: 'within',
        icon: 'favorite',
      },
    ];
  }
  if (offer.stayedBefore) {
    const count = offer.stayCount ?? 0;
    return [
      {
        id: 'stayed-before',
        label: count >= 2 ? copy.stayedBeforeCount(count) : copy.stayedBefore,
        // Sólido como o recomendado: é o selo que faz parar no card.
        tone: 'within',
        icon: 'stayed',
      },
    ];
  }
  // #6359: o hotel com ACORDO da empresa fica acima do eixo — o acordo é a
  // regra da empresa, o eixo é curadoria genérica —, e abaixo dos selos
  // pessoais, que são do viajante. Como os pessoais, não entra no Map do
  // `hotelAxisByOffer`: o eixo que este card ganhou some da tela sem ser doado.
  if (offer.agreedRate) {
    return [{ id: 'agreed-rate', label: copy.agreedRate, tone: 'within', icon: 'agreement' }];
  }
  if (!axis) return [];
  return [
    {
      id: axis,
      label: copy[(axisOnCard ? AXIS_LABEL_ON_CARD : AXIS_LABEL)[axis]],
      tone: axis === 'recommended' ? 'within' : 'unknown',
      // O eixo é o mesmo dos três valores do `HotelBadgeIcon`, então o glifo sai
      // daqui direto: a marca da VOLL no recomendado, etiqueta no menor preço,
      // cronômetro no mais perto.
      icon: axis,
    },
  ];
}

/** O selo do destaque do agente (`directives.highlightOfferIds`) — reusa o
 *  desenho SÓLIDO do recomendado (`icon: 'recommended'`), sem prop nova no
 *  ui-kit: o glifo da marca VOLL já é o que diz "isto é uma escolha
 *  curada", e aqui a curadoria é do agente em vez do fornecedor. */
export function agentPickBadge(copy: HotelBadgesCopy): HotelBadgeView {
  return {
    id: 'agent-pick',
    label: copy.agentPick,
    tone: 'within',
    icon: 'recommended',
  };
}
